import React, { useState, useEffect, ComponentType } from 'react';
import { Grid, Button } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import shortid from 'shortid';
import FormElement from './FormElement';
import ICommonFormProps from './interfaces/ICommonFormProps';
import FormAlongsideText from './FormComponents/FormAlongsideText';

interface IDynamicFormExtenderProps extends ICommonFormProps {
    // Component: React.FunctionComponent<any>;
    // createNewObjectFunction: () => any;
}

export interface IDynamicFormExtenderChildren {
    index: string;
    onChangeHandler: (index: string, key: string, value: string) => void;
    onDeleteHandler: (index: string) => void;
    disabled?: boolean;
}

export interface IDynamicObject {
    [key: string]: any;
}

/**
 * Pass through a form component or a combination of form components
 * that you want to extend dynamically within the form
 * allows you to add the dynamic form data to the form object
 * */
const DynamicFormExtender = (props: IDynamicFormExtenderProps) => {
    if (props.formProps?.createNewObjectFunction === undefined) {
        console.warn('Missing new object function!');
    }
    if (props.formProps?.Component === undefined) {
        console.warn('Missing component!');
    }
    const [dynamicObject, setDynamicObject] = useState<IDynamicObject>({
        [shortid.generate()]: props.formProps?.createNewObjectFunction!(),
    });
    const { t } = useTranslation();

    useEffect(() => {
        if (props.setValue) {
            props.setValue(props.keyName, { ...dynamicObject }, { shouldDirty: true });
        }
        if (props.errors) {
            props.triggerValidation!(props.keyName);
        }
        // eslint-disable-next-line
    }, [dynamicObject]);
    const mapObject = () => {
        const keys = Object.keys(dynamicObject || {});
        const Component = props.formProps?.Component as ComponentType;
        return keys.map((key) => {
            return (
                <Component
                    index={key}
                    key={key}
                    {...dynamicObject[key]}
                    onChangeHandler={onChangeHandler}
                    onDeleteHandler={deleteOption}
                    disabled={props.disabled}
                    getValues={props.getValues}
                    watch={props.watch}
                    keyName={props.keyName}
                    register={props.register}
                    registerOptions={props.registerOptions}
                />
            );
        });
    };
    const onChangeHandler = (index: string, key: string, value: string) => {
        const dynamicObj: any = dynamicObject[index];
        dynamicObj[key] = value;
        addToObject(index, { ...dynamicObj });
    };
    const newObject = () => {
        addToObject(shortid.generate(), props.formProps?.createNewObjectFunction!());
    };
    const addToObject = (key: string, dynamicObj: any) =>
        setDynamicObject((prevState: IDynamicObject) => ({
            ...prevState,
            [key]: dynamicObj,
        }));

    const deleteOption = (index: string) => {
        const dynamicObj = dynamicObject;
        delete dynamicObj[index];
        setDynamicObject({ ...dynamicObj });
    };

    return (
        <FormElement {...props} setValue={(key, value) => setDynamicObject({ ...value })}>
            <Grid container spacing={1} className={props.className}>
                <Grid item xs={12}>
                    <FormAlongsideText
                        label={props.label}
                        secondLabel={props.formProps?.secondLabel as string}
                        secondLabelJSX={props.formProps?.secondLabelJSX}
                    />
                </Grid>
                {props.formProps?.rowColumns &&
                    dynamicObject &&
                    Object.keys(dynamicObject).length > 0 &&
                    props.formProps?.rowColumns}
                {mapObject()}
                <Grid item xs={12}>
                    {!props.disabled && (
                        <Button
                            data-component-name={'EXTENDER_BUTTON'}
                            onClick={newObject}
                            disabled={props.disabled}
                            variant="outlined"
                            style={{ color: '#212121', borderColor: '#C1CAE1!important' }}
                        >
                            {' '}
                            <Add style={{ paddingRight: '0.625rem', fontSize: '2rem' }} />{' '}
                            {t(props.formProps?.buttonLabel as string)}
                        </Button>
                    )}
                </Grid>
                {!Object.keys(dynamicObject).length && (
                    <Grid>
                        <p>{props.formProps?.message}</p>
                    </Grid>
                )}
            </Grid>
        </FormElement>
    );
};

export default DynamicFormExtender;
